import React, { createContext, useCallback, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'

import { Alert } from '@plurall/elo-beta'

import {
  ResponsibleLinkStudentConfirmationModal,
  ResponsibleLinkStudentModal,
  ResponsibleLinkStudentSuccessModal,
} from 'App/components/ResponsibleLinkStudent'
import { graphqlClient, inputValidatePublicCode } from 'utils'
import { ROUTES } from 'utils/constants'
import { ADD_DEPENDENT } from 'graphql/mutations'
import { GET_DEPENDENT_BY_PUBLIC_CODE } from 'graphql/queries'
import { handleGraphQLErrorsWithMessage } from 'utils/graphqlClient'

export const ResponsibleLinkStudentModalContext = createContext({})

const ResponsibleLinkStudentModalProvider = ({ children }) => {
  const [isResponsibleLinkStudentModalOpen, setIsResponsibleLinkStudentModalOpen] = useState(false)
  const [isResponsibleLinkStudentConfirmationOpen, setIsResponsibleLinkStudentConfirmationOpen] =
    useState(false)
  const [isResponsibleLinkStudentSuccessModalOpen, setIsResponsibleLinkStudentSuccessModalOpen] =
    useState(false)
  const [isErrorAlertOpen, setIsErrorAlertOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [studentPublicCode, setStudentPublicCode] = useState('')
  const [student, setStudent] = useState(null)
  const [errors, setErrors] = useState(null)
  const [hasToRefresh, setHasToRefresh] = useState(false)
  const navigate = useNavigate()

  const formatStudent = data => [
    {
      id: data.uuid,
      cells: [
        {
          id: 1,
          value: data.name.trim().split(' ')[0],
        },
      ],
      menu: [],
    },
  ]

  const cancelResponsibleLinkStudent = useCallback(() => {
    setIsResponsibleLinkStudentSuccessModalOpen(false)
    setIsResponsibleLinkStudentConfirmationOpen(false)
    setIsLoading(false)
    setStudentPublicCode('')
    setStudent(null)
    setIsResponsibleLinkStudentModalOpen(true)
    setErrors(null)
    setIsErrorAlertOpen(false)
  }, [])

  const validatePublicCode = useCallback(async () => {
    setErrors(null)
    try {
      const validatePublicCodeValue = inputValidatePublicCode(studentPublicCode)

      if (validatePublicCodeValue) {
        throw new Error(validatePublicCodeValue)
      }

      setIsLoading(true)
      const { data } = await graphqlClient().query({
        query: GET_DEPENDENT_BY_PUBLIC_CODE,
        variables: {
          publicCode: studentPublicCode,
        },
      })

      setIsResponsibleLinkStudentModalOpen(false)

      setStudent(formatStudent(data.person))

      setIsResponsibleLinkStudentConfirmationOpen(true)
    } catch (error) {
      const graphqlError = handleGraphQLErrorsWithMessage(
        error,
        'Não conseguimos localizar o código inserido. Verifique se o código foi digitado corretamente e tente outra vez. Se o problema persistir, entre em contato com o nosso suporte.',
      )

      setErrors(graphqlError[0])
    } finally {
      setIsLoading(false)
    }
  }, [studentPublicCode])

  const linkStudentToResponsible = useCallback(async () => {
    setIsLoading(true)

    try {
      await graphqlClient().mutate({
        mutation: ADD_DEPENDENT,
        variables: {
          dependentUuid: student[0].id,
        },
      })

      setIsResponsibleLinkStudentConfirmationOpen(false)
      setIsResponsibleLinkStudentSuccessModalOpen(true)
      setHasToRefresh(true)
    } catch (error) {
      const graphqlError = handleGraphQLErrorsWithMessage(
        error,
        'Não conseguimos localizar o código inserido. Verifique se o código foi digitado corretamente e tente outra vez. Se o problema persistir, entre em contato com o nosso suporte.',
      )

      setErrors(graphqlError[0])
      setIsResponsibleLinkStudentConfirmationOpen(false)
      setIsErrorAlertOpen(true)
    } finally {
      setIsLoading(false)
    }
  }, [student])

  const handleCloseResponsibleLinkStudentModal = useCallback(() => {
    setIsResponsibleLinkStudentModalOpen(false)
    setErrors(null)
  }, [])

  return (
    <ResponsibleLinkStudentModalContext.Provider
      value={{ setIsResponsibleLinkStudentModalOpen, hasToRefresh, setHasToRefresh }}
    >
      {isResponsibleLinkStudentModalOpen && (
        <ResponsibleLinkStudentModal
          onClose={handleCloseResponsibleLinkStudentModal}
          onSubmit={validatePublicCode}
          onInputChange={setStudentPublicCode}
          isLoading={isLoading}
          validation={errors}
        />
      )}

      {isResponsibleLinkStudentConfirmationOpen && (
        <ResponsibleLinkStudentConfirmationModal
          onClose={() => setIsResponsibleLinkStudentConfirmationOpen(false)}
          students={student}
          onConfirm={linkStudentToResponsible}
          isLoading={isLoading}
          onCancel={cancelResponsibleLinkStudent}
        />
      )}

      {isResponsibleLinkStudentSuccessModalOpen && (
        <ResponsibleLinkStudentSuccessModal
          onClose={() => setIsResponsibleLinkStudentSuccessModalOpen(false)}
          onCancel={cancelResponsibleLinkStudent}
          onConfirm={() => navigate(ROUTES.PERFIL_ALUNOS_VINCULADOS)}
          student={student[0]}
        />
      )}

      {isErrorAlertOpen && (
        <Alert
          title={'Erro ao vincular aluno'}
          secondaryButtonText={'Cancelar'}
          primaryButtonText={'Tentar novamente'}
          onSecondaryClick={cancelResponsibleLinkStudent}
          onPrimaryClick={cancelResponsibleLinkStudent}
          variant={'negative'}
        >
          {errors}
        </Alert>
      )}
      {children}
    </ResponsibleLinkStudentModalContext.Provider>
  )
}

const useResponsibleLinkStudentModal = () => {
  return useContext(ResponsibleLinkStudentModalContext)
}

ResponsibleLinkStudentModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export { ResponsibleLinkStudentModalProvider, useResponsibleLinkStudentModal }
